
import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  Chip,
  Link,
  CircularProgress,
  Alert
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { OrderRoomOccupant } from '../../types/orderRoomOccupant';
import { formatDateFromYYYYMMDD } from '../../../lib/datetime';
import { OrderFormProps } from '../../lib/order';
import { fetchOrder} from '../../lib/order';
import {OrderRoom} from '../../types/orderRoom';
import { Order } from '../../types/order';
import { APIHOST } from '../../common';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useApp } from '../../../src/components/AppContext';
import { getToken } from '../../components/auth/AuthContext';

export default function OrderSummary({
  orderId,
  back,
  onSave,
  type
}: OrderFormProps) {
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { setSnackbarInput } = useApp();

  useEffect(() => {
    fetchOrder(orderId)
      .then((data) => {
        setOrder(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching order:', err);
        setError('Failed to load order summary. Please try again later.');
        setLoading(false);
      });
  }, [orderId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading order summary...
        </Typography>
      </Box>
    );
  }

  if (error || !order) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error || 'Order not found. Please check your order details and try again.'}
      </Alert>
    );
  }

  const submit = async () => {
    try {
      const authToken = getToken();
      if (!authToken) {
        console.error('No authentication token found');
        return;
      }

      const response = await fetch(`${APIHOST}/api/orders/submit?id=${orderId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to submit order');
      }
      setSnackbarInput({ message: 'Order submitted successfully', severity: 'success' });
      console.log('Order submitted successfully');
      if (onSave){
        onSave();
      }

    } catch (error) {
      console.error('Error submitting order:', error);
      setSnackbarInput({
        message: 'Failed to submit order. Please try again later.',
        severity: 'error',
      });
    }
  };

  return (
    <Box>
      
      {/* Conference Information Section */}
      {order.conference_group && <Box py={3}>
        
            
            
            <Typography variant="h6" fontWeight="600">
              {order.conference_group.conference.longName}
            </Typography>
        </Box>
      }


      {/* Contact Information Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="600" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            <CheckCircleIcon color="success" sx={{ ml: 1 }} />
            Contact
          </Typography>
          
          <Box sx={{ display: 'grid', gap: 1 }}>
            {(order.contactFirstName || order.contactLastName) && (
              <Typography variant="body2">
                <strong>Name:</strong> {order.contactFirstName} {order.contactLastName}
              </Typography>
            )}
            
            {order.contactEmail && (
              <Typography variant="body2">
                <strong>Email:</strong> {order.contactEmail}
              </Typography>
            )}
            
            {order.contactCell && (
              <Typography variant="body2">
                <strong>Phone:</strong> {order.contactCell}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Billing Information Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="600" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            <CheckCircleIcon color="success" sx={{ ml: 1 }} />
            Billing
          </Typography>
          
          <Box sx={{ display: 'grid', gap: 1 }}>
            {order.billingAddressee && (
              <Typography variant="body2">
                <strong>Billing Name:</strong> {order.billingAddressee}
              </Typography>
            )}
            
            {order.billingStreet1 && (
              <Typography variant="body2">
                <strong>Address:</strong> {order.billingStreet1}
                {order.billingStreet2 && `, ${order.billingStreet2}`}
              </Typography>
            )}
            
            {(order.billingCity || order.billingCountry) && (
              <Typography variant="body2">
                <strong>City/Country:</strong> {order.billingCity}
                {order.billingCountry && `, ${order.billingCountry}`}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Transportation Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="600" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            <CheckCircleIcon color="success" sx={{ ml: 1 }} />
            Transportation
          </Typography>
          
          <Box sx={{ display: 'grid', gap: 1 }}>
            
            {order.requiresBusParking && (
              <Typography variant="body2">
                <strong>Bus Parking:</strong> Required
              </Typography>
            )}
            
            {order.requiresTransitToVenue && (
              <Typography variant="body2">
                <strong>Transit to Venue:</strong> Required
              </Typography>
            )}
            
            {!order.requiresBusParking && !order.requiresTransitToVenue && (
              <Typography variant="body2" color="text.secondary">
                No transportation requirements specified
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Room & Occupants Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="600" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            <CheckCircleIcon color="success" sx={{ ml: 1 }} />
            Rooms & Occupants
          </Typography>
         
          <Box sx={{ display: 'grid', gap: 2 }}>
            {order.order_rooms && order.order_rooms.length > 0 ? (
              <>
                <Typography variant="body2">
                  <strong>Total Rooms:</strong> {order.order_rooms.length}
                </Typography>
                {order.order_rooms.map((room: OrderRoom, index: number) => (
                  <Box key={index} sx={{ pl: 2, borderLeft: '2px solid', borderColor: 'primary.light', ml: 1 }}>
                    <Typography variant="body2" fontWeight="600">
                      Room {index + 1}
                    </Typography>
                    
                    {room.type && (
                      <Typography variant="body2">
                        <strong>Type:</strong> {room.type}
                      </Typography>
                    )}
                    
                    {room.arrivalDate && (
                      <Typography variant="body2">
                        <strong>Check-in Date:</strong> {formatDateFromYYYYMMDD(room.arrivalDate)}
                      </Typography>
                    )}
                    
                    {room.departureDate && (
                      <Typography variant="body2">
                        <strong>Check-out Date:</strong> {formatDateFromYYYYMMDD(room.departureDate)}
                      </Typography>
                    )}

                    {room.order_room_occupants && room.order_room_occupants.length > 0 && (
                      <>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          <strong>Occupants ({room.order_room_occupants.length}):</strong>
                        </Typography>
                        {room.order_room_occupants.map((occupant: OrderRoomOccupant, occupantIndex: number) => (
                          <Typography key={occupantIndex} variant="body2" sx={{ ml: 2 }}>
                            • {occupant.firstName} {occupant.lastName}
                         
                          </Typography>
                        ))}
                      </>
                    )}
                    
                    
                  </Box>
                ))}
                {order.notesForHotel && (
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        <strong>Special Requests:</strong> {order.notesForHotel}
                      </Typography>
                    )}
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No rooms selected
              </Typography>
            )}
            
          
            
            {order.notesForHotel && (
              <Typography variant="body2">
                <strong>General Notes:</strong> {order.notesForHotel}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Hotel Preferences Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="600" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            <CheckCircleIcon color="success" sx={{ ml: 1 }} />
            Hotel Preferences
          </Typography>
          
          <Box sx={{ display: 'grid', gap: 1 }}>
            {order.rewardsNumber ? (
              <Typography variant="body2">
                <strong>Rewards/Loyalty Number:</strong> {order.rewardsNumber}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No rewards/loyalty number provided
              </Typography>
            )}
            
            {order.termsAccepted ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip 
                  label="Terms & Conditions Accepted" 
                  color="success" 
                  size="small" 
                  icon={<CheckCircleIcon />}
                />
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Terms & conditions not yet accepted
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Hotel Information Section */}
      {order?.conference_hotel?.hotel && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight="600" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              <CheckCircleIcon color="success" sx={{ ml: 1 }} />
              Hotel
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="body1" fontWeight="600">
                {order.conference_hotel.hotel.longName}
              </Typography>
              
              {(order.conference_hotel.hotel.addressCity || order.conference_hotel.hotel.addressState || order.conference_hotel.hotel.addressZip) && (
                <Typography variant="body2" color="text.secondary">
                  {order.conference_hotel.hotel.addressCity}
                  {order.conference_hotel.hotel.addressState && `, ${order.conference_hotel.hotel.addressState}`}
                  {order.conference_hotel.hotel.addressZip && ` ${order.conference_hotel.hotel.addressZip}`}
                </Typography>
              )}
              
              {order.conference_hotel.hotel.website && (
                <Link href={order.conference_hotel.hotel.website} target="_blank" rel="noopener noreferrer" sx={{ fontSize: '0.875rem' }}>
                  Visit Hotel Website
                </Link>
              )}
            </Box>
          </CardContent>
        </Card>
      )}
      <Box>
        <Button
          color="primary"
          size="small"
          startIcon={<ArrowBackIcon fontSize="small" />}
          sx={{px: 4, py: 0.5, mt: 2}}
          onClick={back} 
          disabled={!back} >
          Back
        </Button>
        <Button
          color="primary"
          size="small"
          variant="contained"
          startIcon={<ArrowForwardIcon fontSize="small" />}
          sx={{px: 4, py: 0.5, mt: 2}}
          onClick={submit}
        >
          Submit
        </Button>
      </Box>
    </Box>
  );
}